import { type User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

/**
 * Конфигурация OIDC-клиента для Keycloak.
 *
 * Authorization Code + PKCE — стандартный flow для SPA.
 * Токены хранятся в localStorage. Это допустимо для нашего сценария
 * (работа в доверенной среде); при повышенных требованиях к безопасности
 * можно переключиться на silent-renew + httpOnly cookies через backend.
 */
const userManager = new UserManager({
  authority: `${import.meta.env.VITE_KEYCLOAK_URL}/realms/${import.meta.env.VITE_KEYCLOAK_REALM}`,
  client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: 'openid profile email',
  loadUserInfo: true,
  automaticSilentRenew: true,
  silent_redirect_uri: `${window.location.origin}/auth/silent-renew.html`,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
});

export { userManager };
export type { User };

/**
 * Запустить логин — редиректит на Keycloak.
 * После успеха Keycloak вернёт пользователя на /auth/callback.
 */
export function startLogin(returnTo?: string): Promise<void> {
  return userManager.signinRedirect({
    state: returnTo ? { returnTo } : undefined,
  });
}

/**
 * Завершить логин на странице callback.
 * Возвращает пользователя и URL, куда идти после.
 */
export async function completeLogin(): Promise<{ user: User; returnTo: string }> {
  const user = await userManager.signinRedirectCallback();
  const state = user.state as { returnTo?: string } | undefined;
  return { user, returnTo: state?.returnTo ?? '/' };
}

/**
 * Глобальный logout — редиректит на Keycloak, чистит сессию там и тут.
 */
export function startLogout(): Promise<void> {
  return userManager.signoutRedirect();
}

/**
 * Прочитать текущего пользователя из storage.
 * Возвращает null, если пользователь не залогинен.
 */
export function getStoredUser(): Promise<User | null> {
  return userManager.getUser();
}
